'use client';import { GarageAccount } from '@parkingbase/ecommerce-api-test';


import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { MdSearch } from 'react-icons/md';

import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Button } from '@/components/ui/button/Button';
import {
  ButtonSize,
  ButtonVariant,
} from '@/components/ui/button/Button.types';
import { Input } from '@/components/ui/input/Input';
import { DatePicker } from '@/components/ui/date-picker';
import { Status } from '@/components/ui/status/Status';
import { StatusState } from '@/components/ui/status/Status.types';
import { useAuthStore } from '@/stores/user.store';
import { useParkingValidationStore } from '@/stores/parking-validation.store';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { useToast } from '@/hooks/useToast';
import { extractApiErrorMessage } from '@/lib/api-error-utils';
import { getContactName } from '@/lib/contact-utils';
import { SHEET_MENU_Z_INDEX } from '@/lib/styleUtils';
import {
  toGarageZonedISOString,
  resolveTimezoneId,
  getDurationMinutes,
} from '@/lib/date-time-utils';
import { ChargeToAccountModalProps } from './ChargeToAccount.types';

/** Deterministic avatar color from a name string. */
const AVATAR_COLORS = [
  '#28ccc9',
  '#ccab28',
  '#d973b2',
  '#6abc75',
  '#7b8fd4',
  '#e08a5e',
  '#9c7ec2',
  '#5bb8c9',
];
function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.[0] ?? '').toUpperCase();
}

function formatDuration(minutes: number): string {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}hrs`;
  return `${hrs}hrs ${mins}m`;
}

/**
 * Charge to Account modal — two-step BottomSheet flow:
 * 1. Choose account (search + list of garage accounts)
 * 2. Confirm charge (account header, exit time, rate, submit)
 */
export function ChargeToAccountModal({
  ticket,
  isOpen,
  onClose,
  onSuccess,
}: ChargeToAccountModalProps) {
  const t = useTranslations('ParkingValidation');
  const tCommon = useTranslations('Common');
  const { getRecaptchaHeader } = useRecaptcha();
  const { showSuccess } = useToast();

  const garageAccounts = useAuthStore((s) => s.garageAccounts);
  const chargeToAccount = useParkingValidationStore(
    (s) => s.chargeToAccount
  );
  const chargeToAccountLoading = useParkingValidationStore(
    (s) => s.chargeToAccountLoading
  );
  const fetchTickets = useParkingValidationStore((s) => s.fetchTickets);
  const getOpenTicketRate = useParkingValidationStore((s) => s.getOpenTicketRate);

  const [step, setStep] = useState<'choose' | 'confirm'>('choose');
  const [selectedAccount, setSelectedAccount] =
    useState<GarageAccount | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [exitTime, setExitTime] = useState<Date | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('choose');
      setSelectedAccount(null);
      setSearchQuery('');
      setExitTime(null);
      setRate(null);
      setRateLoading(false);
      setError(null);
    }
  }, [isOpen]);

  // Filter active garage accounts by search
  const filteredAccounts = useMemo(() => {
    const active = garageAccounts.filter((a) => a.status === 'ACTIVE');
    if (!searchQuery.trim()) return active;
    const query = searchQuery.toLowerCase();
    return active.filter((a) => {
      const name = getContactName(a.monthlyAccount?.contact);
      const accountNum = a.monthlyAccount?.accountNumber ?? '';
      const garageName = a.garage?.name ?? '';
      return (
        name.toLowerCase().includes(query) ||
        accountNum.toLowerCase().includes(query) ||
        garageName.toLowerCase().includes(query)
      );
    });
  }, [garageAccounts, searchQuery]);

  /**
   * Fetches the live rate for the open ticket up to `exit` via the rate engine
   * (mirrors the legacy reservation site's fetchRate). Called on account select
   * and whenever the exit time changes.
   */
  const fetchRate = useCallback(
    async (exit: Date, timezone?: string) => {
      if (!ticket.ticketId) return;
      setRateLoading(true);
      try {
        const options = await getRecaptchaHeader('OPEN_TICKET_RATE');
        const result = await getOpenTicketRate(
          ticket.ticketId,
          toGarageZonedISOString(exit, timezone),
          ticket.company,
          options
        );
        setRate(result?.rate ?? 0);
      } catch (err) {
        setError(extractApiErrorMessage(err, t('chargeToAccountError')));
        setRate(null);
      } finally {
        setRateLoading(false);
      }
    },
    [ticket.ticketId, ticket.company, getOpenTicketRate, getRecaptchaHeader, t]
  );

  const handleSelectAccount = useCallback(
    (account: GarageAccount) => {
      setSelectedAccount(account);
      const now = new Date();
      setExitTime(now);
      setStep('confirm');
      setError(null);
      fetchRate(now, resolveTimezoneId(account.garage?.timezone));
    },
    [fetchRate]
  );

  const handleChangeAccount = useCallback(() => {
    setStep('choose');
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const enterDate = ticket.enter ? new Date(ticket.enter) : null;

  const duration = useMemo(() => {
    if (!enterDate || !exitTime) return '';
    const mins = getDurationMinutes(enterDate, exitTime);
    return mins > 0 ? formatDuration(mins) : '';
  }, [enterDate, exitTime]);

  const handleCharge = useCallback(async () => {
    if (!selectedAccount || !ticket.ticketId || !exitTime || rate == null) return;
    try {
      setError(null);
      const options = await getRecaptchaHeader('CHARGE_TO_ACCOUNT');
      await chargeToAccount(
        ticket.ticketId,
        selectedAccount.accountId,
        rate,
        toGarageZonedISOString(exitTime, resolveTimezoneId(selectedAccount.garage?.timezone)),
        ticket.company,
        options
      );
      const refreshOptions = await getRecaptchaHeader('GET_TICKETS');
      await fetchTickets(refreshOptions);
      showSuccess(t('chargeToAccountSuccess'));
      onSuccess();
    } catch (err) {
      setError(
        extractApiErrorMessage(err, t('chargeToAccountError'))
      );
    }
  }, [
    selectedAccount,
    ticket,
    exitTime,
    rate,
    chargeToAccount,
    fetchTickets,
    onSuccess,
    getRecaptchaHeader,
    showSuccess,
    t,
  ]);

  const title =
    step === 'choose'
      ? t('chooseAccount')
      : t('chargeToAccountTitle');

  return (
    <BottomSheet
      open={isOpen}
      onClose={handleClose}
      title={title}
      bodyClassName="tw:p-base"
    >
      <div data-cy="charge-to-account-modal">
        {error && (
          <div
            className="tw:mb-base tw:p-small tw:bg-error/10 tw:text-error tw:text-sm tw:rounded-4"
            role="alert"
            data-cy="charge-error"
          >
            {error}
          </div>
        )}

        {/* Step 1: Choose account */}
        {step === 'choose' && (
          <div className="tw:flex tw:flex-col tw:gap-base">
            <Input
              value={searchQuery}
              onChange={(val) => setSearchQuery(val)}
              placeholder={t('searchPlaceholder')}
              leftIcon={<MdSearch size={20} aria-hidden="true" />}
              hideBottomPadding
              data-cy="charge-account-search"
            />
            <div
              className="tw:flex tw:flex-col tw:gap-small"
              data-cy="charge-account-list"
            >
              {filteredAccounts.map((account) => {
                const name = getContactName(
                  account.monthlyAccount?.contact
                );
                const initials = getInitials(name);
                const color = getAvatarColor(name);
                const garageName = account.garage?.name ?? '';
                const accountNum =
                  account.monthlyAccount?.accountNumber ?? '';

                return (
                  <div
                    key={account.accountId}
                    className="tw:flex tw:items-center tw:gap-base tw:px-base tw:py-small tw:border tw:border-solid tw:border-gray-light tw:rounded-8"
                    data-cy="charge-account-card"
                  >
                    <div className="tw:flex tw:flex-1 tw:items-center tw:gap-small tw:min-w-0">
                      <div
                        className="tw:flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-full tw:shrink-0 tw:text-white tw:text-14 tw:font-bold"
                        // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (avatar color derived from name)
                        style={{ backgroundColor: color }}
                      >
                        {initials}
                      </div>
                      <div className="tw:flex tw:flex-col tw:gap-nano tw:min-w-0">
                        <span className="tw:text-14 tw:font-bold tw:text-font tw:truncate">
                          {name || '—'}
                        </span>
                        <span className="tw:text-14 tw:text-gray-darker tw:truncate">
                          {garageName}
                          {accountNum && ` · ${accountNum}`}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant={ButtonVariant.secondary}
                      size={ButtonSize.small}
                      onClick={() => handleSelectAccount(account)}
                      className="tw:shrink-0"
                      data-cy="charge-account-select"
                    >
                      {t('changeDiscountSelect')}
                    </Button>
                  </div>
                );
              })}
              {filteredAccounts.length === 0 && (
                <div className="tw:p-base tw:text-center tw:text-gray-dark" data-cy="charge-accounts-empty">
                  {t('noAccountsFound')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Confirm charge */}
        {step === 'confirm' && selectedAccount && (
          <div className="tw:flex tw:flex-col tw:gap-base">
            {/* Selected account header */}
            <div className="tw:flex tw:items-center tw:gap-base tw:pb-base tw:border-solid tw:border-0 tw:border-b tw:border-gray-light">
              <div className="tw:flex tw:flex-1 tw:items-center tw:gap-small tw:min-w-0">
                <div
                  className="tw:flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-full tw:shrink-0 tw:text-white tw:text-14 tw:font-bold"
                  // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (avatar color derived from name)
                  style={{
                    backgroundColor: getAvatarColor(
                      getContactName(
                        selectedAccount.monthlyAccount?.contact
                      )
                    ),
                  }}
                >
                  {getInitials(
                    getContactName(
                      selectedAccount.monthlyAccount?.contact
                    )
                  )}
                </div>
                <div className="tw:flex tw:flex-col tw:gap-nano tw:min-w-0">
                  <div className="tw:flex tw:items-center tw:gap-micro">
                    <span className="tw:text-16 tw:font-bold tw:text-font tw:truncate" data-cy="charge-selected-account-name">
                      {getContactName(
                        selectedAccount.monthlyAccount?.contact
                      ) || '—'}
                    </span>
                    <Status
                      status={StatusState.positive}
                      text={tCommon('active')}
                    />
                  </div>
                  <span className="tw:text-14 tw:text-gray-darker tw:truncate">
                    {selectedAccount.garage?.name ?? ''}
                    {selectedAccount.monthlyAccount?.accountNumber &&
                      ` · ${selectedAccount.monthlyAccount.accountNumber}`}
                  </span>
                </div>
              </div>
              <Button
                variant={ButtonVariant.secondary}
                size={ButtonSize.small}
                onClick={handleChangeAccount}
                className="tw:shrink-0"
                data-cy="charge-change-account"
              >
                {t('chargeToAccountChange')}
              </Button>
            </div>

            {/* Rate card: exit time + rate/duration */}
            <div className="tw:border tw:border-solid tw:border-gray-light tw:rounded-8 tw:p-base tw:flex tw:items-center tw:gap-huge">
              <div className="tw:shrink-0 tw:w-[234px]">
                <DatePicker
                  label={t('chargeToAccountExitTime')}
                  value={exitTime}
                  onChange={(date) => {
                    setExitTime(date);
                    if (date)
                      fetchRate(date, resolveTimezoneId(selectedAccount.garage?.timezone));
                  }}
                  enableTime
                  timeInterval={15}
                  minDate={enterDate ?? undefined}
                  dateFormat="h:mm aa, MM/dd/yyyy"
                  hideBottomPadding
                  data-cy="charge-exit-time"
                  menuZIndex={SHEET_MENU_Z_INDEX}
                />
              </div>
              <div className="tw:border-solid tw:border-0 tw:border-l tw:border-gray-light tw:h-16 tw:shrink-0" />
              <div className="tw:flex tw:flex-col tw:gap-nano">
                <span className="tw:text-14 tw:text-gray-darker">
                  {t('chargeToAccountRate')}
                </span>
                <span
                  className="tw:text-20 tw:font-bold tw:text-font"
                  data-cy="charge-rate-amount"
                >
                  {rateLoading ? '…' : `$${(rate ?? 0).toFixed(2)}`}
                </span>
                {duration && (
                  <span
                    className="tw:text-14 tw:text-gray-dark"
                    data-cy="charge-duration"
                  >
                    {t('chargeToAccountDuration', { duration })}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="tw:flex tw:gap-base tw:pt-huge">
              <Button
                variant={ButtonVariant.secondary}
                size={ButtonSize.large}
                onClick={handleClose}
                disabled={chargeToAccountLoading}
                className="tw:flex-1"
                data-cy="charge-cancel"
              >
                {tCommon('cancel')}
              </Button>
              <Button
                variant={ButtonVariant.primary}
                size={ButtonSize.large}
                onClick={handleCharge}
                disabled={chargeToAccountLoading || rateLoading || !exitTime || rate == null}
                isLoading={chargeToAccountLoading}
                className="tw:flex-1"
                data-cy="charge-submit"
              >
                {t('chargeToAccountButton')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
